import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, FileText, Building2, Home as HomeIcon, Phone, Info } from 'lucide-react';
import { useDynamicSEO } from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOCAL_PAGES = [
  { to: '/immobilien-verkaufen-st-gallen', label: 'Immobilien verkaufen in St. Gallen' },
  { to: '/immobilien-kaufen-st-gallen', label: 'Immobilien kaufen in St. Gallen' },
  { to: '/immobilienmakler-gossau', label: 'Immobilienmakler in Gossau SG' },
  { to: '/haus-kaufen-gossau', label: 'Haus kaufen in Gossau SG' },
  { to: '/immobilienmakler-wil-sg', label: 'Immobilienmakler in Wil SG' },
  { to: '/immobilien-wil-sg', label: 'Immobilien in Wil SG' },
  { to: '/immobilienmakler-herisau', label: 'Immobilienmakler in Herisau' },
  { to: '/immobilienmakler-rorschach', label: 'Immobilienmakler in Rorschach' },
];

const MAIN_PAGES = [
  { to: '/', label: 'Startseite', icon: HomeIcon },
  { to: '/ueber-uns', label: 'Über uns', icon: Info },
  { to: '/projekte', label: 'Projekte', icon: Building2 },
  { to: '/strategie', label: 'Strategie & Werte', icon: Info },
  { to: '/blog', label: 'Blog & Ratgeber', icon: FileText },
  { to: '/immobilien-verkauf', label: 'Immobilie verkaufen', icon: Building2 },
  { to: '/immobilien-kauf', label: 'Immobilie kaufen', icon: Building2 },
  { to: '/renditeobjekt-kaufen', label: 'Renditeobjekt kaufen', icon: Building2 },
  { to: '/renditeobjekt-verkaufen', label: 'Renditeobjekt verkaufen', icon: Building2 },
  { to: '/kontakt', label: 'Kontakt', icon: Phone },
];

const LEGAL_PAGES = [
  { to: '/datenschutz', label: 'Datenschutz' },
  { to: '/impressum', label: 'Impressum' },
];

export const Sitemap = () => {
  const [projects, setProjects] = useState([]);
  const [posts, setPosts] = useState([]);

  useDynamicSEO({
    title: 'Sitemap – Übersicht aller Seiten | LIVAG AG',
    description: 'Vollständige Übersicht aller Seiten der LIVAG AG – Immobilien, Projekte, Blog-Artikel und Regionen in der Ostschweiz.',
    canonical: 'https://www.livag.ch/sitemap',
  });

  useEffect(() => {
    fetch(`${API_URL}/api/projects`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setProjects(Array.isArray(data) ? data.filter(p => p.visible !== false) : []))
      .catch(() => {});
    fetch(`${API_URL}/api/blog/posts/public`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setPosts(Array.isArray(data) ? data : (data.posts || [])))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sitemap-page">
      <section className="bg-[#264F77] text-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-normal mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Sitemap
          </h1>
          <p className="text-lg text-white/80 max-w-3xl">
            Übersicht aller Seiten der LIVAG AG – Ihre nachhaltige Immobilienentwicklung in der Ostschweiz.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Hauptseiten */}
          <div>
            <h2 className="text-2xl font-semibold text-[#264F77] mb-6 flex items-center gap-2">
              <HomeIcon size={22} /> Hauptseiten
            </h2>
            <ul className="space-y-2">
              {MAIN_PAGES.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="flex items-center gap-3 text-gray-700 hover:text-[#264F77] py-2 border-b border-gray-100 transition-colors"
                  >
                    <Icon size={16} className="text-gray-400" /> {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Regionen */}
          <div>
            <h2 className="text-2xl font-semibold text-[#264F77] mb-6 flex items-center gap-2">
              <MapPin size={22} /> Regionen & Standorte
            </h2>
            <ul className="space-y-2">
              {LOCAL_PAGES.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="flex items-center gap-3 text-gray-700 hover:text-[#264F77] py-2 border-b border-gray-100 transition-colors"
                  >
                    <MapPin size={16} className="text-gray-400" /> {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Projekte */}
          {projects.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-[#264F77] mb-6 flex items-center gap-2">
                <Building2 size={22} /> Projekte
              </h2>
              <ul className="space-y-2">
                {projects.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/projekte/${p.id}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#264F77] py-2 border-b border-gray-100 transition-colors"
                    >
                      <Building2 size={16} className="text-gray-400" />
                      <span>{p.title}{p.location ? ` – ${p.location}` : ''}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Blog */}
          {posts.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold text-[#264F77] mb-6 flex items-center gap-2">
                <FileText size={22} /> Blog & Ratgeber
              </h2>
              <ul className="space-y-2">
                {posts.map((post) => (
                  <li key={post.slug || post.id}>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="flex items-center gap-3 text-gray-700 hover:text-[#264F77] py-2 border-b border-gray-100 transition-colors"
                    >
                      <FileText size={16} className="text-gray-400" /> {post.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Rechtliches */}
          <div>
            <h2 className="text-2xl font-semibold text-[#264F77] mb-6">Rechtliches</h2>
            <ul className="space-y-2">
              {LEGAL_PAGES.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="block text-gray-700 hover:text-[#264F77] py-2 border-b border-gray-100 transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sitemap;
